import React, { useEffect, useMemo, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import {
  entitlementsApiService,
  type AdminEntitlement,
  type EntitlementDecision,
} from '@/shared/services/entitlementsApiService';
import { PAGE_KEYS, CONFIGURABLE_PAGE_KEYS, DEFAULT_ACCESS_TIER, type AccessTier } from '@/shared/config/pageKeys';
import { logger, dateUtils } from '@/shared/utils';
import type { AdminUser } from '@/shared/types/admin';
import toast from 'react-hot-toast';

const PAGE_KEY_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  drafts: 'Drafts',
  profile: 'Profile',
  settings: 'Settings',
  calculator: 'Calculator',
  toastmasters: 'Toastmasters',
  blog_create: 'Create Blog',
  blog_edit: 'Edit Blog',
  blog_preview: 'Blog Preview',
  'admin.dashboard': 'Admin Dashboard',
  'admin.users': 'Admin: Users',
  'admin.blogs': 'Admin: Blogs',
  'admin.analytics': 'Admin: Analytics',
  'admin.portfolio': 'Admin: Portfolio',
  'admin.flashcards': 'Admin: Flashcards',
  'admin.activity': 'Admin: Activity',
  'admin.errors': 'Admin: Errors',
  'admin.entitlements': 'Admin: Entitlements',
};

const pageKeyLabel = (key: string) => PAGE_KEY_LABELS[key] || key;

type DecisionFilter = 'all' | EntitlementDecision;

const AdminEntitlementsPage: React.FC = () => {
  const theme = useSafeThemeClasses();

  // Current entitlements table
  const [entitlements, setEntitlements] = useState<AdminEntitlement[]>([]);
  const [total, setTotal] = useState(0);
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Users, for the picker
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Bulk action form
  const [pageKey, setPageKey] = useState<string>(PAGE_KEYS.ADMIN_ANALYTICS);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Who currently has denies for the selected page - used to filter the
  // picker down to users who can actually see it right now.
  const [deniedUserIds, setDeniedUserIds] = useState<Set<string>>(new Set());
  const [deniedLoading, setDeniedLoading] = useState(true);

  // Page access tiers (public/user/admin) - the base default for the whole
  // user base, separate from the per-user grant/deny table below.
  const [pageLevels, setPageLevels] = useState<Record<string, AccessTier>>({});
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [savingLevel, setSavingLevel] = useState<string | null>(null);

  const loadPageLevels = async () => {
    setLevelsLoading(true);
    try {
      const response = await entitlementsApiService.getPageLevels();
      if (response.success && response.data) {
        setPageLevels(response.data.levels);
      }
    } catch (err) {
      logger.error('Failed to load page access levels', 'AdminEntitlementsPage', { error: err });
    } finally {
      setLevelsLoading(false);
    }
  };

  useEffect(() => {
    loadPageLevels();
  }, []);

  const handleSetPageLevel = async (key: string, level: AccessTier) => {
    setSavingLevel(key);
    try {
      const response = await entitlementsApiService.setPageLevel(key, level);
      if (response.success) {
        setPageLevels((prev) => ({ ...prev, [key]: level }));
        toast.success(`${pageKeyLabel(key)} is now ${level === 'public' ? 'public' : level === 'user' ? 'open to all users' : 'admin-only'}`);
      } else {
        toast.error(response.error?.message || 'Failed to update page access level');
      }
    } catch (err) {
      logger.error('Failed to set page access level', 'AdminEntitlementsPage', { error: err, key, level });
      toast.error('Failed to update page access level');
    } finally {
      setSavingLevel(null);
    }
  };

  const loadEntitlements = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await entitlementsApiService.listEntitlements({
        decision: decisionFilter === 'all' ? undefined : decisionFilter,
        per_page: 100,
      });
      if (response.success && response.data) {
        setEntitlements(response.data.entitlements);
        setTotal(response.data.pagination.total);
      } else {
        setError(response.error?.message || 'Failed to load entitlements');
        logger.error('Failed to load entitlements', 'AdminEntitlementsPage', { error: response.error });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entitlements');
      logger.error('Network error loading entitlements', 'AdminEntitlementsPage', { error: err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntitlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisionFilter]);

  useEffect(() => {
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const response = await adminApiService.getUsers({ per_page: 200 });
        if (response.success && response.data) {
          setUsers(response.data.users);
        }
      } catch (err) {
        logger.error('Failed to load users for entitlements picker', 'AdminEntitlementsPage', { error: err });
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Refetch denials whenever the selected page changes - the eligible
  // list (who currently can see this specific page) depends on it.
  useEffect(() => {
    let cancelled = false;
    const loadDenied = async () => {
      setDeniedLoading(true);
      try {
        const response = await entitlementsApiService.listEntitlements({
          page_key: pageKey,
          decision: 'deny',
          per_page: 500,
        });
        if (!cancelled && response.success && response.data) {
          setDeniedUserIds(new Set(response.data.entitlements.map((e) => e.user_id)));
        }
      } catch (err) {
        logger.error('Failed to load current denials for page', 'AdminEntitlementsPage', { error: err, pageKey });
      } finally {
        if (!cancelled) setDeniedLoading(false);
      }
    };
    loadDenied();
    setSelectedUserIds(new Set());
    return () => {
      cancelled = true;
    };
  }, [pageKey]);

  // Only users worth offering in the picker. For true admin.* pages, a
  // grant is inert (the outer role gate blocks non-admins before an
  // entitlement is ever checked), so non-admins are excluded entirely -
  // offering to deny/grant something they categorically can't see anyway
  // is meaningless. Configurable pages (dashboard, drafts, ...) don't get
  // that exclusion even at 'admin' tier, because THAT'S exactly what a
  // grant is for there: letting one specific non-admin through without
  // opening the page to everyone. Anyone already denied is excluded
  // either way - they can't see the page regardless of tier or role.
  const eligibleUsers = useMemo(() => {
    const isAdminPage = pageKey.startsWith('admin.');
    return users.filter((u) => {
      if (isAdminPage && !u.is_admin && !u.is_god) return false;
      if (deniedUserIds.has(u.id)) return false;
      return true;
    });
  }, [users, pageKey, deniedUserIds]);

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedUserIds((prev) => {
      if (prev.size === eligibleUsers.length) return new Set();
      return new Set(eligibleUsers.map((u) => u.id));
    });
  };

  const selectionCount = selectedUserIds.size;

  const handleBulkAction = async (decision: EntitlementDecision) => {
    if (selectedUserIds.size === 0) {
      toast.error('Select at least one user');
      return;
    }
    setSubmitting(true);
    try {
      const response = await entitlementsApiService.bulkSetEntitlements(pageKey, decision, {
        userIds: Array.from(selectedUserIds),
      });
      if (response.success && response.data) {
        toast.success(
          `${decision === 'grant' ? 'Granted' : 'Denied'} ${pageKeyLabel(pageKey)} for ${response.data.success_count} user(s)`
        );
        setSelectedUserIds(new Set());
        loadEntitlements();
        // The set of users who can currently see this page just changed.
        setDeniedUserIds((prev) => {
          const next = new Set(prev);
          for (const id of selectedUserIds) {
            if (decision === 'deny') next.add(id);
            else next.delete(id);
          }
          return next;
        });
      } else {
        toast.error(response.error?.message || 'Bulk action failed');
      }
    } catch (err) {
      logger.error('Bulk entitlement action failed', 'AdminEntitlementsPage', { error: err });
      toast.error('Bulk action failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (row: AdminEntitlement) => {
    try {
      const response = await entitlementsApiService.deleteEntitlement(row.id);
      if (response.success) {
        toast.success('Entitlement removed - back to default-allow');
        loadEntitlements();
        if (row.page_key === pageKey && row.decision === 'deny') {
          setDeniedUserIds((prev) => {
            const next = new Set(prev);
            next.delete(row.user_id);
            return next;
          });
        }
      } else {
        toast.error(response.error?.message || 'Failed to remove entitlement');
      }
    } catch (err) {
      logger.error('Failed to remove entitlement', 'AdminEntitlementsPage', { error: err });
      toast.error('Failed to remove entitlement');
    }
  };

  const pageKeyOptions = useMemo(() => Object.values(PAGE_KEYS), []);

  if (loading && usersLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.card} rounded-2xl p-12 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading entitlements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-1 ${theme.textHeading} tracking-tight`}>Entitlements</h2>
        <p className={theme.textSecondary}>
          Feature pages are admin-only until opened up below. Set a page's base access, then use
          per-user grants or denies for exceptions.
        </p>
      </div>

      {/* Page access tiers - the base default for the whole user base.
          Per-user grant/deny below layers on top of this. */}
      <div className={`${theme.card} rounded-3xl p-6 space-y-4`}>
        <div>
          <h3 className={`text-lg font-semibold ${theme.textHeading}`}>Page access</h3>
          <p className={`text-sm ${theme.textSecondary}`}>
            Public: anyone. User: any logged-in user. Admin: admin/god only (default), unless a specific user is granted below.
          </p>
        </div>
        {levelsLoading ? (
          <p className={`text-sm ${theme.textMuted}`}>Loading...</p>
        ) : (
          <div className="space-y-2">
            {CONFIGURABLE_PAGE_KEYS.map((key) => {
              const currentLevel = pageLevels[key] ?? DEFAULT_ACCESS_TIER;
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between gap-4 p-3 rounded-lg ${theme.input}`}
                >
                  <span className={`text-sm font-medium ${theme.textPrimary}`}>{pageKeyLabel(key)}</span>
                  <div className="flex gap-1.5">
                    {(['admin', 'user', 'public'] as AccessTier[]).map((tier) => (
                      <button
                        key={tier}
                        onClick={() => handleSetPageLevel(key, tier)}
                        disabled={savingLevel === key || currentLevel === tier}
                        className={`px-3 py-1 rounded-lg text-xs font-medium capitalize transition disabled:cursor-default ${
                          currentLevel === tier
                            ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
                            : `${theme.buttonSecondary} ${theme.textSecondary} hover:opacity-80`
                        }`}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div className={`${theme.alertError} rounded-xl p-4`}>
          <p className={theme.textError}>{error}</p>
        </div>
      )}

      {/* Single outer glass panel - grant/deny form and the current-rules
          table used to be two separate stacked theme.card sections, which
          on this page (taller than any other admin tab) doubled up the
          blur/gradient/border layers enough to read as a stray second
          background behind the content. One panel with an internal divider
          keeps the same layout with a single glass surface. */}
      <div className={`${theme.card} rounded-3xl p-6 space-y-4`}>
        <h3 className={`text-lg font-semibold ${theme.textHeading}`}>Grant or deny a page</h3>

        <div className="space-y-2">
          <label className={`block text-sm font-medium ${theme.textPrimary}`}>Page</label>
          <select
            value={pageKey}
            onChange={(e) => setPageKey(e.target.value)}
            className={`w-full p-3 rounded-xl ${theme.input} ${theme.textPrimary}`}
          >
            {pageKeyOptions.map((key) => (
              <option key={key} value={key}>{pageKeyLabel(key)}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className={`block text-sm font-medium ${theme.textPrimary}`}>
            Users who currently have access
          </label>
          <p className={`text-xs ${theme.textMuted}`}>
            {pageKey.startsWith('admin.')
              ? 'Only admins can see this page, so only admins are listed here.'
              : 'Everyone can see this page by default; anyone already denied is left off this list.'}
          </p>

          {deniedLoading || usersLoading ? (
            <div className={`rounded-xl ${theme.input} p-4 text-center text-sm ${theme.textMuted}`}>
              Loading users...
            </div>
          ) : eligibleUsers.length === 0 ? (
            <div className={`rounded-xl ${theme.input} p-4 text-center text-sm ${theme.textMuted}`}>
              No users currently have access to this page.
            </div>
          ) : (
            <div className={`rounded-xl ${theme.input} max-h-64 overflow-y-auto p-2 space-y-1`}>
              <label className={`flex items-center gap-2 px-2 py-1.5 text-sm font-medium ${theme.textPrimary}`}>
                <input
                  type="checkbox"
                  checked={selectedUserIds.size === eligibleUsers.length}
                  onChange={toggleSelectAllVisible}
                />
                Select all ({eligibleUsers.length})
              </label>
              {eligibleUsers.map((u) => (
                <label
                  key={u.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-opacity hover:opacity-75 ${theme.textSecondary}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.has(u.id)}
                    onChange={() => toggleUser(u.id)}
                  />
                  {u.username} <span className={theme.textMuted}>({u.email})</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <span className={`text-sm ${theme.textMuted}`}>
            {selectionCount} user{selectionCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('grant')}
              disabled={submitting}
              className={`px-4 py-2 rounded-xl transition ${theme.buttonPrimary} ${theme.buttonTextPrimary} disabled:opacity-50`}
            >
              Grant Access
            </button>
            <button
              onClick={() => handleBulkAction('deny')}
              disabled={submitting}
              className={`px-4 py-2 rounded-xl transition ${theme.alertError} disabled:opacity-50`}
            >
              Deny Access
            </button>
          </div>
        </div>
      </div>

      {/* Current entitlements table - lives inside the same glass panel as
          the form above via a plain divider, not a second theme.card. */}
      <div className={`border-t ${theme.separator} pt-4 space-y-4`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h3 className={`text-xl font-semibold ${theme.textHeading}`}>Current Rules</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2">
              {([
                { id: 'all', label: 'All' },
                { id: 'deny', label: 'Denied' },
                { id: 'grant', label: 'Granted' },
              ] as const).map((filterOption) => (
                <button
                  key={filterOption.id}
                  onClick={() => setDecisionFilter(filterOption.id)}
                  className={`px-3 py-1.5 rounded-xl text-sm transition ${
                    decisionFilter === filterOption.id
                      ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
                      : `${theme.buttonSecondary} ${theme.textSecondary}`
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
            <span className={`text-sm ${theme.textMuted}`}>{total.toLocaleString()} total</span>
          </div>
        </div>

        {entitlements.length === 0 ? (
          <p className={`text-center py-8 ${theme.textMuted}`}>
            No explicit rules yet - every page is visible to everyone by default.
          </p>
        ) : (
          <div className="space-y-2">
            {entitlements.map((row) => (
              <div
                key={row.id}
                className={`flex items-center justify-between gap-4 p-3 rounded-lg ${theme.input} ${theme.textPrimary}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${row.decision === 'deny' ? theme.alertError : theme.alertSuccess}`}>
                      {row.decision === 'deny' ? 'Denied' : 'Granted'}
                    </span>
                    <span className={`text-sm font-medium ${theme.textHeading}`}>{pageKeyLabel(row.page_key)}</span>
                  </div>
                  <p className={`text-xs ${theme.textMuted}`}>
                    {row.user_username || row.user_email || row.user_id}
                    {row.granted_by_username && ` · by ${row.granted_by_username}`}
                    {row.updated_at && ` · ${dateUtils.format(row.updated_at, 'SHORT')}`}
                  </p>
                </div>
                <button
                  onClick={() => handleRemove(row)}
                  className={`text-xs px-3 py-1.5 rounded-lg shrink-0 transition ${theme.buttonSecondary} ${theme.textSecondary}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEntitlementsPage;
